import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, XCircle, Info } from 'lucide-react';

// Info tooltip component
const InfoTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-block ml-1">
      <Info
        className="h-4 w-4 text-t4 hover:text-blue-600 dark:hover:text-blue-400 cursor-help inline"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
      />
      {show && (
        <div className="absolute z-50 w-64 p-3 bg-surface-0 text-t1 text-xs rounded-lg shadow-lg border border-line -top-2 left-6">
          {text}
          <div className="absolute left-0 top-3 -ml-2 border-8 border-transparent border-r-slate-900" />
        </div>
      )}
    </div>
  );
};

interface InventoryAnalyticsProps {
  data: any;
}

const InventoryAnalytics: React.FC<InventoryAnalyticsProps> = ({ data }) => {
  const { t } = useTranslation(['analytics', 'common']);

  if (!data) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/5 rounded-2xl border border-blue-500/20 p-4 sm:p-5">
          <p className="text-xs sm:text-sm font-medium text-t3 flex items-center mb-1.5">
            {t('analytics:inventory.totalMaterials')}
            <InfoTooltip text={t('analytics:inventory.totalMaterialsTooltip')} />
          </p>
          <div className="text-xl sm:text-2xl font-bold text-t1">{data.total_materials || 0}</div>
          <div className="text-xs text-t4 mt-1">
            {t('analytics:inventory.value', { amount: formatCurrency(data.total_value || 0) })}
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500/10 to-yellow-500/5 rounded-2xl border border-amber-500/20 p-4 sm:p-5">
          <p className="text-xs sm:text-sm font-medium text-t3 flex items-center mb-1.5">
            {t('analytics:inventory.lowStock')}
            <InfoTooltip text={t('analytics:inventory.lowStockTooltip')} />
          </p>
          <div className="text-xl sm:text-2xl font-bold text-yellow-700 dark:text-yellow-400">
            {data.low_stock?.length || 0}
          </div>
          <div className="text-xs text-t4 mt-1">
            {t('analytics:inventory.needsOrder')}
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 rounded-2xl border border-red-500/20 p-4 sm:p-5">
          <p className="text-xs sm:text-sm font-medium text-t3 flex items-center mb-1.5">
            {t('analytics:inventory.outOfStock')}
            <InfoTooltip text={t('analytics:inventory.outOfStockTooltip')} />
          </p>
          <div className="text-xl sm:text-2xl font-bold text-red-600 dark:text-red-400">
            {data.out_of_stock?.length || 0}
          </div>
          <div className="text-xs text-t4 mt-1">
            {t('analytics:inventory.unavailable')}
          </div>
        </div>
      </div>

      {/* Low Stock Items */}
      {data.low_stock?.length > 0 && (
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-700 dark:text-yellow-400" />
              {t('analytics:inventory.lowStockItems')}
            </h3>
            <p className="text-sm text-t3 mt-1">{t('analytics:inventory.belowMinimum')}</p>
          </div>
          <div className="p-5 space-y-2">
            {/* Χωρίς κουμπί «Παραγγελία τώρα» (14/10/2026): δεν έκανε τίποτα — δεν υπάρχει ροή παραγγελίας σε
                προμηθευτή (η Αποθήκη έχει μόνο «Καταγραφή αγοράς», μετά την παραλαβή). */}
            {data.low_stock.map((item: any, idx: number) => (
              <div key={idx} className="p-3 border border-yellow-500/30 rounded-xl bg-yellow-500/10">
                <p className="font-medium text-t1">{item.name}</p>
                <p className="text-sm text-t3">
                  {t('analytics:inventory.currentMinimum', { current: item.current, unit: item.unit, minimum: item.minimum })}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Out of Stock Items */}
      {data.out_of_stock?.length > 0 && (
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              {t('analytics:inventory.outOfStockItems')}
            </h3>
            <p className="text-sm text-t3 mt-1">{t('analytics:inventory.needsAttention')}</p>
          </div>
          <div className="p-5 space-y-2">
            {/* Ούτε «Επείγουσα παραγγελία»: το ίδιο νεκρό κουμπί. */}
            {data.out_of_stock.map((item: any, idx: number) => (
              <div key={idx} className="p-3 border border-red-500/30 rounded-xl bg-red-500/10">
                <p className="font-medium text-t1">{item.name}</p>
                <p className="text-sm text-t3">
                  {item.last_purchase
                    ? t('analytics:inventory.lastPurchase', { date: new Date(item.last_purchase).toLocaleDateString('el-GR') })
                    : t('analytics:inventory.lastPurchaseNever')}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Consumption Rate */}
      {data.consumption_rate?.length > 0 && (
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1">{t('analytics:inventory.consumptionRate')}</h3>
            <p className="text-sm text-t3 mt-1">{t('analytics:inventory.dailyUsageAndDaysLeft')}</p>
          </div>
          <div className="p-5">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-line">
                    <th className="text-left py-2 text-t3 text-sm font-medium">{t('analytics:inventory.materialColumn')}</th>
                    <th className="text-right py-2 text-t3 text-sm font-medium">{t('analytics:inventory.dailyUsageColumn')}</th>
                    <th className="text-right py-2 text-t3 text-sm font-medium">{t('analytics:inventory.daysRemainingColumn')}</th>
                    <th className="text-right py-2 text-t3 text-sm font-medium">{t('analytics:inventory.statusColumn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.consumption_rate.map((item: any, idx: number) => (
                    <tr key={idx} className="border-b border-line/50">
                      <td className="py-2 text-t1">{item.material}</td>
                      <td className="text-right py-2 text-t2">{item.daily_usage.toFixed(2)}</td>
                      <td className="text-right py-2 font-medium text-t1">
                        {item.days_remaining === -1 ? '∞' : item.days_remaining}
                      </td>
                      <td className="text-right py-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          item.days_remaining > 30 ? 'bg-green-500/20 text-green-600 dark:text-green-400' :
                          item.days_remaining > 7 ? 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400' :
                          'bg-red-500/20 text-red-600 dark:text-red-400'
                        }`}>
                          {item.days_remaining > 30 ? t('analytics:inventory.statusGood') :
                           item.days_remaining > 7 ? t('analytics:inventory.statusLow') :
                           t('analytics:inventory.statusCritical')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Performance */}
      {data.supplier_performance?.length > 0 && (
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1">{t('analytics:inventory.supplierPerformance')}</h3>
          </div>
          <div className="p-5">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-line">
                    <th className="text-left py-2 text-t3 text-sm font-medium">{t('analytics:inventory.supplierColumn')}</th>
                    <th className="text-right py-2 text-t3 text-sm font-medium">{t('analytics:inventory.ordersColumn')}</th>
                    <th className="text-right py-2 text-t3 text-sm font-medium">{t('analytics:inventory.totalSpentColumn')}</th>
                    <th className="text-right py-2 text-t3 text-sm font-medium">{t('analytics:inventory.avgDeliveryColumn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.supplier_performance.map((supplier: any, idx: number) => (
                    <tr key={idx} className="border-b border-line/50">
                      <td className="py-2 text-t1">{supplier.supplier}</td>
                      <td className="text-right py-2 text-t2">{supplier.orders}</td>
                      <td className="text-right py-2 text-t1">{formatCurrency(supplier.total_spent)}</td>
                      <td className="text-right py-2 text-t2">{supplier.average_delivery_time} {t('analytics:inventory.days')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Η «Ανάλυση απωλειών» αφαιρέθηκε (13/10/2026): ο server έστελνε πάντα 0 € / 0% (κόκκινα). Η φύρα
          που καταγράφεται είναι η αναφορά «Φύρα» (WasteAnalytics), δίπλα στο Απόθεμα της Αρχικής. Και η στήλη
          «Αξιοπιστία» των προμηθευτών: ήταν σταθερό 90%. */}
    </div>
  );
};

export default InventoryAnalytics;
